// ============================================================
//  NOTIFICATION STORE
// ============================================================

import { create }                          from 'zustand';
import { ref, set as fbSet, remove, onValue, off }  from 'firebase/database';
import { db }                              from '../firebase/config';
import { generateId }                      from '../utils';
import type { FleetNotification, Crew }    from '../types';

interface NotificationState {
  list:             FleetNotification[];
  seen:             Set<string>;
  initialLoadDone:  boolean;

  onSnapshot:      (notifs: FleetNotification[]) => void;
  checkIncoming:   (crews: Crew[]) => void;
  markAllRead:     () => void;
  clearAll:        () => void;
  pushNotif:       (n: FleetNotification) => void;
  clearNotif:      (id: string) => void;

  startSync: () => () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  list:            [],
  seen:            new Set<string>(),
  initialLoadDone: false,

  onSnapshot(notifs) {
    const sorted = [...notifs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const seen = new Set(get().seen);
    sorted.forEach(n => { if (n.key) seen.add(n.key); });
    set({ list: sorted, seen, initialLoadDone: true });
  },

  checkIncoming(crews) {
    if (!get().initialLoadDone) return;
    const seen = new Set(get().seen);
    const list = [...get().list];
    let changed = false;

    crews.forEach(c => {
      (['driver', 'sideman'] as const).forEach(role => {
        const m = c[role];
        if (m.confirmed && m.confirmedAt) {
          const key = `${c.id}_${role}_confirmed`;
          if (!seen.has(key) && !list.find(n => n.key === key)) {
            seen.add(key);
            const n: FleetNotification = {
              id: generateId(), key, type: 'confirmed',
              crewId: c.id, name: m.name, role,
              partner: role === 'driver' ? c.sideman.name : c.driver.name,
              workDate: c.workDate, createdAt: m.confirmedAt!,
            };
            fbSet(ref(db, `notifications/${n.id}`), n);
            list.unshift(n);
            changed = true;
          }
        }
        if (m.declined) {
          const key = `${c.id}_${role}_declined`;
          if (!seen.has(key) && !list.find(n => n.key === key)) {
            seen.add(key);
            const n: FleetNotification = {
              id: generateId(), key, type: 'declined',
              crewId: c.id, name: m.name, role,
              note: m.declineNote || '',
              workDate: c.workDate, createdAt: new Date().toISOString(),
            };
            fbSet(ref(db, `notifications/${n.id}`), n);
            list.unshift(n);
            changed = true;
          }
        }
      });
    });

    if (changed) set({ list, seen });
  },

  markAllRead() {
    const list = get().list.map(n => {
      if (!n.readAt) {
        const updated = { ...n, readAt: new Date().toISOString() };
        fbSet(ref(db, `notifications/${n.id}`), updated);
        return updated;
      }
      return n;
    });
    set({ list });
  },

  clearAll() {
    get().list.forEach(n => remove(ref(db, `notifications/${n.id}`)));
    set({ list: [] });
  },

  pushNotif(n) { fbSet(ref(db, `notifications/${n.id}`), n); },
  clearNotif(id) { remove(ref(db, `notifications/${id}`)); },

  startSync() {
    const notifRef = ref(db, 'notifications');
    const unsub = onValue(notifRef, snap => {
      const raw = snap.val() || {};
      const notifs: FleetNotification[] = Object.values(raw);
      get().onSnapshot(notifs);
    });
    return () => off(notifRef, 'value', unsub);
  },
}));
